import { useEffect, useRef, useState, useCallback } from "react";
import * as d3 from "d3";
import { getSkillGapData, swapSkillGapTitle } from "./services/api";
import { RefreshCw, ZoomIn, ZoomOut, X } from "lucide-react";

// Theme palette
const COLOR = {
  center: "#402c7a",
  centerText: "#ffffff",
  job: "#4338ca",
  jobText: "#ffffff",
  known: "#6a56a7",
  knownText: "#ffffff",
  gapFill: "#ffffff",
  gapStroke: "#402c7a",
  gapText: "#402c7a",
  link: "#c4b5fd",
  bg: "#f6faff",
};

const RADIUS = { center: 56, skill: 36 };
const JOB_RECT = { w: 116, h: 52, rx: 10 };

function buildGraph(jobTopics) {
  const nodes = [{ id: "you", label: "You", type: "center" }];
  const links = [];
  // normalised label → skillId, so shared skills reuse the same node
  const skillMap = {};

  Object.entries(jobTopics).forEach(([title, topics], ji) => {
    const jobId = `job_${ji}`;
    nodes.push({ id: jobId, label: title, type: "job", slot: ji });
    links.push({ source: "you", target: jobId, strength: 0.6 });

    topics.forEach((topic) => {
      const key = topic.name.toLowerCase().trim();
      if (skillMap[key]) {
        // shared skill — add another link to this job title
        links.push({ source: jobId, target: skillMap[key], strength: 0.4 });
      } else {
        const skillId = `skill_${key.replace(/[^a-z0-9]/g, "_")}`;
        skillMap[key] = skillId;
        nodes.push({
          id: skillId,
          label: topic.name,
          type: "skill",
          known: topic.known,
        });
        links.push({ source: jobId, target: skillId, strength: 0.4 });
      }
    });
  });

  return { nodes, links };
}

// Greedy word-wrap that fits text inside a circle of the given radius.
// Returns an array of line strings (max 3 lines; last line truncated with "…" if needed).
function wrapLines(text, radius, fontSize) {
  const charW = fontSize * 0.58; // avg char width estimate
  const maxW = Math.floor((radius * 1.4) / charW); // usable chord (< diameter)
  const maxLines = radius <= 36 ? 3 : 2;

  // Split into tokens, hard-breaking any single token that exceeds maxW
  const tokens = [];
  for (const w of text.split(" ")) {
    if (w.length <= maxW) {
      tokens.push(w);
    } else {
      // Hard-break long tokens at maxW characters
      let rem = w;
      while (rem.length > maxW) {
        tokens.push(rem.slice(0, maxW - 1) + "-");
        rem = rem.slice(maxW - 1);
      }
      if (rem) tokens.push(rem);
    }
  }

  const lines = [];
  let cur = "";
  for (const w of tokens) {
    const candidate = cur ? `${cur} ${w}` : w;
    if (candidate.length > maxW && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = candidate;
    }
  }
  if (cur) lines.push(cur);

  if (lines.length <= maxLines) return lines;

  // Truncate: keep maxLines, mark last with "…"
  const truncated = lines.slice(0, maxLines);
  const last = truncated[maxLines - 1];
  truncated[maxLines - 1] =
    last.length > maxW - 1
      ? last.slice(0, maxW - 1).trimEnd() + "…"
      : last + "…";
  return truncated;
}

function applyTextLines(el, lines, fontSize) {
  if (lines.length === 1) {
    el.text(lines[0]);
    return;
  }
  el.text(null);
  const lh = fontSize * 1.25;
  const startDy = -((lines.length - 1) / 2) * lh;
  lines.forEach((line, i) => {
    el.append("tspan")
      .attr("x", 0)
      .attr("dy", i === 0 ? startDy : lh)
      .text(line);
  });
}

const CACHE_KEY = (email) => `skillgap_topics_${email}`;

function saveTopicsCache(email, jobTopics) {
  try {
    sessionStorage.setItem(CACHE_KEY(email), JSON.stringify(jobTopics));
  } catch (_) {}
}

function loadTopicsCache(email) {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY(email));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

// Reconstruct the plain jobTopics map from a graphData object
function graphDataToTopics(gd) {
  const topics = {};
  const jobNodes = gd.nodes.filter((n) => n.type === "job");
  jobNodes.forEach((j) => {
    const skills = gd.links
      .filter(
        (l) =>
          (l.source.id || l.source) === j.id ||
          (l.target.id || l.target) === j.id,
      )
      .map((l) => {
        const otherId =
          (l.source.id || l.source) === j.id
            ? l.target.id || l.target
            : l.source.id || l.source;
        return gd.nodes.find((n) => n.id === otherId);
      })
      .filter((n) => n && n.type === "skill")
      .map((n) => ({ name: n.label, known: n.known }));
    topics[j.label] = skills;
  });
  return topics;
}

export default function SkillGapScreen({ userEmail, navigateTo }) {
  const svgRef = useRef(null);
  const zoomRef = useRef(null);
  const svgSelRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [idle, setIdle] = useState(() => loadTopicsCache(userEmail) === null);
  const [error, setError] = useState(null);
  const [empty, setEmpty] = useState(() => {
    const cached = loadTopicsCache(userEmail);
    return cached !== null && Object.keys(cached).length === 0;
  });
  const [graphData, setGraphData] = useState(() => {
    const cached = loadTopicsCache(userEmail);
    return cached && Object.keys(cached).length > 0 ? buildGraph(cached) : null;
  });
  const [swapTarget, setSwapTarget] = useState(null); // { slot, currentTitle }
  const [swapUrl, setSwapUrl] = useState("");
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapError, setSwapError] = useState(null);

  // Stable ref so d3 click handlers always see the latest setter
  const onSwapClickRef = useRef(null);
  onSwapClickRef.current = (slot, currentTitle) => {
    setSwapTarget({ slot, currentTitle });
    setSwapUrl("");
    setSwapError(null);
  };

  const handleZoomIn = useCallback(() => {
    if (svgSelRef.current && zoomRef.current)
      svgSelRef.current
        .transition()
        .duration(300)
        .call(zoomRef.current.scaleBy, 1.4);
  }, []);

  const handleZoomOut = useCallback(() => {
    if (svgSelRef.current && zoomRef.current)
      svgSelRef.current
        .transition()
        .duration(300)
        .call(zoomRef.current.scaleBy, 1 / 1.4);
  }, []);

  const handleSwapSubmit = useCallback(async () => {
    if (!swapTarget || !swapUrl.trim()) return;
    setSwapLoading(true);
    setSwapError(null);
    try {
      const res = await swapSkillGapTitle(userEmail, swapUrl.trim());
      if (!res || !res.title) {
        setSwapError("Could not parse a job title from that URL.");
        return;
      }
      if (!res.skills || res.skills.length === 0) {
        setSwapError(
          "Found the title but couldn't extract any skills. The page may require login or be dynamically rendered.",
        );
        return;
      }
      // Replace the slot in graphData and rebuild
      setGraphData((prev) => {
        if (!prev) return prev;
        // Reconstruct jobTopics shape from current nodes, then swap the target slot
        const jobTopics = {};
        const jobNodes = prev.nodes.filter((n) => n.type === "job");
        jobNodes.forEach((j) => {
          if (j.slot === swapTarget.slot) {
            jobTopics[res.title] = res.skills || [];
          } else {
            const skills = prev.links
              .filter(
                (l) =>
                  (l.source.id || l.source) === j.id ||
                  (l.target.id || l.target) === j.id,
              )
              .map((l) => {
                const otherId =
                  (l.source.id || l.source) === j.id
                    ? l.target.id || l.target
                    : l.source.id || l.source;
                return prev.nodes.find((n) => n.id === otherId);
              })
              .filter((n) => n && n.type === "skill")
              .map((n) => ({ name: n.label, known: n.known }));
            jobTopics[j.label] = skills;
          }
        });
        saveTopicsCache(userEmail, jobTopics);
        return buildGraph(jobTopics);
      });
      setSwapTarget(null);
      setSwapUrl("");
    } catch (err) {
      setSwapError(err.message || "Failed to fetch the job description.");
    } finally {
      setSwapLoading(false);
    }
  }, [swapTarget, swapUrl, userEmail]);

  const closeSwap = useCallback(() => {
    setSwapTarget(null);
    setSwapUrl("");
    setSwapError(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setIdle(false);
    setError(null);
    setEmpty(false);
    try {
      const data = await getSkillGapData(userEmail);
      if (!data.job_topics || Object.keys(data.job_topics).length === 0) {
        setEmpty(true);
        saveTopicsCache(userEmail, {});
      } else {
        saveTopicsCache(userEmail, data.job_topics);
        setGraphData(buildGraph(data.job_topics));
      }
    } catch (err) {
      setError(err.message || "Failed to load skill gap data.");
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (!graphData || !svgRef.current) return;

    const container = svgRef.current.parentElement;
    const W = container.clientWidth || 900;
    const H = Math.max(560, container.clientHeight || 600);

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", W).attr("height", H);

    const g = svg.append("g");

    // Zoom + pan
    const zoom = d3
      .zoom()
      .scaleExtent([0.4, 2.5])
      .on("zoom", (event) => g.attr("transform", event.transform));
    svg.call(zoom);
    zoomRef.current = zoom;
    svgSelRef.current = svg;

    const { nodes, links } = graphData;

    // Triangle anchors: top, bottom-left, bottom-right around centered "You"
    const slotCoords = [
      { x: W / 2, y: H * 0.18 },
      { x: W * 0.22, y: H * 0.78 },
      { x: W * 0.78, y: H * 0.78 },
    ];
    nodes.forEach((n) => {
      if (n.type === "center") {
        n.fx = W / 2;
        n.fy = H / 2;
      } else if (n.type === "job" && slotCoords[n.slot]) {
        n.fx = slotCoords[n.slot].x;
        n.fy = slotCoords[n.slot].y;
      }
    });

    const sim = d3
      .forceSimulation(nodes)
      .force(
        "link",
        d3
          .forceLink(links)
          .id((d) => d.id)
          .distance((d) =>
            d.source === "you" || (d.source && d.source.id === "you")
              ? 200
              : 130,
          )
          .strength((d) => d.strength),
      )
      .force("charge", d3.forceManyBody().strength(-380))
      .force(
        "collision",
        d3
          .forceCollide()
          .radius((d) =>
            d.type === "job" ? JOB_RECT.w / 2 + 12 : RADIUS[d.type] + 10,
          ),
      );

    // Links
    const linkSel = g
      .append("g")
      .selectAll("line")
      .data(links)
      .join("line")
      .attr("stroke", COLOR.link)
      .attr("stroke-width", 2)
      .attr("stroke-opacity", 0.8);

    // Node groups (only skill nodes are draggable; job + center are pinned)
    const nodeSel = g
      .append("g")
      .selectAll("g")
      .data(nodes)
      .join("g")
      .style("cursor", (d) => (d.type === "skill" ? "grab" : "default"));

    nodeSel
      .filter((d) => d.type === "skill")
      .call(
        d3
          .drag()
          .on("start", (event, d) => {
            if (!event.active) sim.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on("drag", (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
          })
          .on("end", (event, d) => {
            if (!event.active) sim.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          }),
      );

    // Circles for center and skill nodes
    nodeSel
      .filter((d) => d.type !== "job")
      .append("circle")
      .attr("r", (d) => RADIUS[d.type])
      .attr("fill", (d) =>
        d.type === "center"
          ? COLOR.center
          : d.known
            ? COLOR.known
            : COLOR.gapFill,
      )
      .attr("stroke", (d) =>
        d.type === "skill" && !d.known ? COLOR.gapStroke : "none",
      )
      .attr("stroke-width", (d) => (d.type === "skill" && !d.known ? 2.5 : 0))
      .attr("filter", (d) => (d.type === "center" ? "url(#shadow)" : null));

    // Rectangles for job nodes
    nodeSel
      .filter((d) => d.type === "job")
      .append("rect")
      .attr("width", JOB_RECT.w)
      .attr("height", JOB_RECT.h)
      .attr("x", -JOB_RECT.w / 2)
      .attr("y", -JOB_RECT.h / 2)
      .attr("rx", JOB_RECT.rx)
      .attr("fill", COLOR.job);

    // Swap icon: small circle in top-right of each job rect
    const swapBtn = nodeSel
      .filter((d) => d.type === "job")
      .append("g")
      .attr("class", "swap-btn")
      .attr(
        "transform",
        `translate(${JOB_RECT.w / 2 - 10}, ${-JOB_RECT.h / 2 + 10})`,
      )
      .style("cursor", "pointer")
      .on("mouseenter", function () {
        d3.select(this).select("circle").attr("fill", "#ede9fe");
      })
      .on("mouseleave", function () {
        d3.select(this).select("circle").attr("fill", "#ffffff");
      })
      .on("mousedown", function () {
        d3.select(this).select("circle").attr("fill", "#c4b5fd");
      })
      .on("mouseup", function () {
        d3.select(this).select("circle").attr("fill", "#ede9fe");
      })
      .on("click", (event, d) => {
        event.stopPropagation();
        if (onSwapClickRef.current) onSwapClickRef.current(d.slot, d.label);
      });

    swapBtn
      .append("circle")
      .attr("r", 9)
      .attr("fill", "#ffffff")
      .attr("stroke", COLOR.job)
      .attr("stroke-width", 1.5);

    // PencilLine lucide icon, scaled from 24×24 to 13×13, centred in the button
    const pencilG = swapBtn
      .append("g")
      .attr("transform", "translate(-6.5,-6.5) scale(0.542)");
    pencilG
      .append("path")
      .attr("d", "M12 20h9")
      .attr("stroke", COLOR.job)
      .attr("stroke-width", 2)
      .attr("stroke-linecap", "round")
      .attr("fill", "none");
    pencilG
      .append("path")
      .attr(
        "d",
        "M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z",
      )
      .attr("stroke", COLOR.job)
      .attr("stroke-width", 2)
      .attr("stroke-linecap", "round")
      .attr("stroke-linejoin", "round")
      .attr("fill", "none");
    pencilG
      .append("path")
      .attr("d", "m15 5 3 3")
      .attr("stroke", COLOR.job)
      .attr("stroke-width", 2)
      .attr("stroke-linecap", "round")
      .attr("fill", "none");

    // Drop shadow for center
    const defs = svg.append("defs");
    const filter = defs.append("filter").attr("id", "shadow");
    filter
      .append("feDropShadow")
      .attr("dx", 0)
      .attr("dy", 3)
      .attr("stdDeviation", 6)
      .attr("flood-color", "#402c7a")
      .attr("flood-opacity", 0.3);

    // Labels
    nodeSel
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("fill", (d) => {
        if (d.type === "center") return COLOR.centerText;
        if (d.type === "job") return COLOR.jobText;
        return d.known ? COLOR.knownText : COLOR.gapText;
      })
      .attr("font-family", (d) =>
        d.type === "center"
          ? "'Zen Dots', sans-serif"
          : "'Segoe UI', sans-serif",
      )
      .attr("font-weight", (d) => (d.type === "center" ? "700" : "600"))
      .attr("font-size", (d) => {
        if (d.type === "center") return "15px";
        if (d.type === "job") return "11px";
        return "10px";
      })
      .each(function (d) {
        const el = d3.select(this);
        const fontSize = d.type === "center" ? 15 : d.type === "job" ? 11 : 10;
        if (d.type === "center") {
          el.text(d.label);
          return;
        }
        const wrapRadius = d.type === "job" ? JOB_RECT.w / 2 : RADIUS[d.type];
        const lines = wrapLines(d.label, wrapRadius, fontSize);
        applyTextLines(el, lines, fontSize);
      });

    sim.on("tick", () => {
      linkSel
        .attr("x1", (d) => d.source.x)
        .attr("y1", (d) => d.source.y)
        .attr("x2", (d) => d.target.x)
        .attr("y2", (d) => d.target.y);

      nodeSel.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });

    return () => sim.stop();
  }, [graphData]);

  return (
    <div>
      <div
        style={{
          maxWidth: "72rem",
          margin: "0 auto",
          padding: "0 1rem",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px 0 16px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div>
            <h1 className="applicant-theme-text">Identify My Gaps</h1>
            <p
              style={{
                color: "#6b7280",
                margin: "4px 0 0",
                fontSize: "0.9rem",
              }}
            >
              Skills you already have vs. what your top job titles require. Drag
              nodes to explore.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              onClick={load}
              disabled={loading}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                background: loading ? "#e5e7eb" : COLOR.center,
                color: loading ? "#9ca3af" : "#fff",
                border: "none",
                borderRadius: "8px",
                fontWeight: "600",
                fontSize: "0.85rem",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "background 0.2s",
              }}
            >
              <RefreshCw
                size={14}
                style={{
                  animation: loading ? "spin 1s linear infinite" : "none",
                }}
              />
              {loading
                ? "Generating…"
                : idle || (!graphData && !empty && !error)
                  ? "Generate"
                  : "Regenerate"}
            </button>
          </div>
        </div>

        {/* Canvas area */}
        <div
          style={{
            flex: 1,
            margin: "0 0 24px",
            borderRadius: "16px",
            background: "#fff",
            boxShadow: "0 2px 16px rgba(64,44,122,0.08)",
            overflow: "hidden",
            position: "relative",
            minHeight: "500px",
          }}
        >
          {loading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px",
                color: COLOR.center,
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  border: `4px solid #e5e7eb`,
                  borderTop: `4px solid ${COLOR.center}`,
                  borderRadius: "50%",
                  animation: "spin 0.9s linear infinite",
                }}
              />
              <p
                style={{
                  fontFamily: "'Zen Dots', sans-serif",
                  fontSize: "0.95rem",
                  color: COLOR.center,
                  margin: 0,
                }}
              >
                Analyzing your skill gaps…
              </p>
            </div>
          )}

          {idle && !loading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                color: "#6b7280",
              }}
            >
              <p
                style={{
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  color: COLOR.center,
                  margin: 0,
                }}
              >
                Ready to analyze your skill gaps
              </p>
              <p
                style={{
                  fontSize: "0.875rem",
                  maxWidth: 340,
                  textAlign: "center",
                  margin: 0,
                }}
              >
                Press <strong>Generate</strong> to see which skills your target
                job titles require and which ones you already have.
              </p>
            </div>
          )}

          {error && !loading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                color: "#a72d2d",
              }}
            >
              <p style={{ fontSize: "1rem", fontWeight: 600 }}>
                Something went wrong
              </p>
              <p
                style={{
                  fontSize: "0.85rem",
                  color: "#6b7280",
                  maxWidth: 320,
                  textAlign: "center",
                }}
              >
                {error}
              </p>
              <button
                onClick={load}
                style={{
                  marginTop: "8px",
                  padding: "8px 20px",
                  background: COLOR.center,
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Try again
              </button>
            </div>
          )}

          {empty && !loading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                color: "#6b7280",
              }}
            >
              <p
                style={{
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  color: COLOR.center,
                }}
              >
                No application history yet
              </p>
              <p
                style={{
                  fontSize: "0.875rem",
                  maxWidth: 320,
                  textAlign: "center",
                }}
              >
                Save at least one job application and this map will show the
                skills you need to bridge the gap.
              </p>
              <button
                onClick={() => navigateTo("history")}
                style={{
                  marginTop: "8px",
                  padding: "8px 20px",
                  background: COLOR.center,
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Go to Applications
              </button>
            </div>
          )}

          {!loading && !idle && !error && !empty && (
            <>
              {/* Zoom toolbar */}
              <div
                style={{
                  position: "absolute",
                  top: 12,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  background: "rgba(255,255,255,0.92)",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  padding: "5px 8px",
                  boxShadow: "0 1px 8px rgba(64,44,122,0.08)",
                  zIndex: 10,
                }}
              >
                <button
                  onClick={handleZoomIn}
                  title="Zoom in"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 30,
                    height: 30,
                    border: "none",
                    borderRadius: "6px",
                    background: "transparent",
                    color: COLOR.center,
                    cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f3f0ff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <ZoomIn size={16} />
                </button>
                <div style={{ width: 1, height: 18, background: "#e5e7eb" }} />
                <button
                  onClick={handleZoomOut}
                  title="Zoom out"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 30,
                    height: 30,
                    border: "none",
                    borderRadius: "6px",
                    background: "transparent",
                    color: COLOR.center,
                    cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f3f0ff")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <ZoomOut size={16} />
                </button>
              </div>

              <svg
                ref={svgRef}
                style={{ width: "100%", height: "100%", display: "block" }}
              />

              {/* Bottom-right legend overlay */}
              <div
                style={{
                  position: "absolute",
                  bottom: 16,
                  right: 16,
                  background: "rgba(255,255,255,0.92)",
                  border: "1px solid #e5e7eb",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "7px",
                  boxShadow: "0 1px 8px rgba(64,44,122,0.08)",
                  pointerEvents: "none",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "10px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "#9ca3af",
                  }}
                >
                  Legend
                </p>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "0.78rem",
                    color: "#374151",
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      display: "inline-block",
                      width: 22,
                      height: 13,
                      borderRadius: "3px",
                      background: COLOR.job,
                    }}
                  />
                  Targeted job position
                </span>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "0.78rem",
                    color: "#374151",
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      display: "inline-block",
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: COLOR.known,
                    }}
                  />
                  Already acquired
                </span>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "0.78rem",
                    color: "#374151",
                  }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      display: "inline-block",
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: COLOR.gapFill,
                      border: `2px solid ${COLOR.gapStroke}`,
                    }}
                  />
                  Missing skill
                </span>
              </div>
            </>
          )}
        </div>

        {/* Note below graph */}
        {!loading && !idle && !error && !empty && (
          <p
            style={{
              margin: "0 0 28px",
              fontSize: "0.82rem",
              color: "#6b7280",
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            <span style={{ fontWeight: 600, color: COLOR.center }}>Tip:</span>{" "}
            Mastered a new skill? Head to your{" "}
            <span
              onClick={() => navigateTo("profile")}
              style={{
                color: COLOR.job,
                fontWeight: 600,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Profile
            </span>{" "}
            and update your resume so your skill map stays accurate.
          </p>
        )}

        {/* Swap-title modal */}
        {swapTarget && (
          <div
            onClick={closeSwap}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(20, 14, 41, 0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "1rem",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff",
                borderRadius: "14px",
                padding: "22px 24px",
                width: "100%",
                maxWidth: "440px",
                boxShadow: "0 8px 32px rgba(64,44,122,0.25)",
                position: "relative",
              }}
            >
              <button
                onClick={closeSwap}
                aria-label="Close"
                style={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: "#9ca3af",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <X size={18} />
              </button>

              <h3
                style={{
                  margin: 0,
                  fontFamily: "'Zen Dots', sans-serif",
                  fontSize: "1.05rem",
                  color: COLOR.center,
                }}
              >
                Replace job title
              </h3>
              <p
                style={{
                  margin: "6px 0 14px",
                  fontSize: "0.85rem",
                  color: "#6b7280",
                }}
              >
                Currently:{" "}
                <strong style={{ color: "#374151" }}>
                  {swapTarget.currentTitle}
                </strong>
                . Paste a job posting URL — we'll pull its title and required
                skills.
              </p>

              <input
                type="url"
                value={swapUrl}
                onChange={(e) => setSwapUrl(e.target.value)}
                placeholder="https://company.com/jobs/12345"
                disabled={swapLoading}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "0.9rem",
                  outline: "none",
                  boxSizing: "border-box",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSwapSubmit();
                }}
              />

              {swapError && (
                <p
                  style={{
                    margin: "10px 0 0",
                    color: "#a72d2d",
                    fontSize: "0.8rem",
                  }}
                >
                  {swapError}
                </p>
              )}

              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  justifyContent: "flex-end",
                  marginTop: "16px",
                }}
              >
                <button
                  onClick={closeSwap}
                  disabled={swapLoading}
                  style={{
                    padding: "8px 16px",
                    background: "#fff",
                    color: "#6b7280",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    cursor: swapLoading ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSwapSubmit}
                  disabled={swapLoading || !swapUrl.trim()}
                  style={{
                    padding: "8px 18px",
                    background:
                      swapLoading || !swapUrl.trim() ? "#e5e7eb" : COLOR.center,
                    color: swapLoading || !swapUrl.trim() ? "#9ca3af" : "#fff",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    cursor:
                      swapLoading || !swapUrl.trim()
                        ? "not-allowed"
                        : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  {swapLoading && (
                    <RefreshCw
                      size={13}
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                  )}
                  {swapLoading ? "Fetching…" : "Replace"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Spin keyframe injected once */}
        <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
      </div>
    </div>
  );
}
